/**
 * URLキャプチャのフォーム (URL・端末) と、撮った後の行き先を選ぶフッターのボタンを載せたダイアログ
 * (docs/features/swipe-motion-clip-plan.md §11.2.8)。入口は素材パネルのヘッダーの1つだけで、フッターで
 * 「スクリーンショット画像を生成」(投入したら閉じてキューで進行を見せる) と「スクロール動画を生成」(完了を待って
 * スクロール動画の編集画面へ進む) を選ぶ。各ボタンの説明はツールチップに置く。
 */
import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { HelpTooltip } from '../../components/common/HelpTooltip';
import { Tooltip } from '../../components/common/Tooltip';
import { isComposingEnter } from '../videoEditor/components/imeKey';
import { normalizeCaptureUrl, type UrlCaptureDevice, type UrlCaptureJobBody } from './api';
import styles from './UrlCaptureForm.module.css';
import '../../styles/buttons.css';

export type DeviceChoice = UrlCaptureDevice | 'both';

const DEVICE_CHOICES: ReadonlyArray<[DeviceChoice, string]> = [
  ['phone', 'スマホ'],
  ['pc', 'PC'],
  ['both', '両方'],
];

function devicesForChoice(choice: DeviceChoice): UrlCaptureDevice[] {
  return choice === 'both' ? ['phone', 'pc'] : [choice];
}

/** フッターの送信ボタン1つ分。並びは配列の順 (主アクションを最後に置く)。 */
export interface UrlCaptureAction {
  label: string;
  /** ボタン上のツールチップ (説明)。 */
  tooltip?: ReactNode;
  /** 主アクション (btn-modal)。それ以外は補助 (btn-modal-secondary) */
  primary?: boolean;
  /** 投入。投げたエラーの message をフォームに出す。 */
  onSubmit: (body: UrlCaptureJobBody) => Promise<void>;
}

export interface UrlCaptureDialogProps {
  title: string;
  /** フォームの上に出す説明。 */
  description?: ReactNode;
  /** 上部の注記 (前回の取得結果など)。 */
  notice?: ReactNode;
  actions: UrlCaptureAction[];
  onClose: () => void;
  /** 呼び出し側の処理中 (キャプチャ完了待ち)。入力と送信を止め、busyText を出す */
  busy?: boolean;
  busyText?: string;
  /** 呼び出し側のエラー (キャプチャの失敗理由)。 */
  error?: string | null;
  /** 端末の既定値 (既定は両方) */
  defaultChoice?: DeviceChoice;
  /** false なら「両方」を出さない (シーンの画像は1枚なので、シーンから撮るときに使う) */
  allowBoth?: boolean;
}

export function UrlCaptureDialog({ title, description, notice, actions, onClose, busy = false, busyText, error, defaultChoice, allowBoth = true }: UrlCaptureDialogProps) {
  const [url, setUrl] = useState('');
  const [choice, setChoice] = useState<DeviceChoice>(defaultChoice ?? (allowBoth ? 'both' : 'phone'));
  const choices = allowBoth ? DEVICE_CHOICES : DEVICE_CHOICES.filter(([value]) => value !== 'both');
  /** 投入中のアクションの添字 (そのボタンだけ「開始しています…」にする) */
  const [submitting, setSubmitting] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const locked = submitting !== null || busy;
  /** Enter で送るのは主アクション (無ければ最後のボタン) */
  const enterIndex = Math.max(0, actions.findIndex((a) => a.primary));

  const handleSubmit = async (index: number) => {
    const action = actions[index];
    if (locked || !action) return;
    const normalized = normalizeCaptureUrl(url);
    if (!normalized) {
      setFormError('httpまたはhttpsのURLを入力してください');
      return;
    }
    setFormError(null);
    setSubmitting(index);
    try {
      await action.onSubmit({ url: normalized, devices: devicesForChoice(choice) });
    } catch (err) {
      setFormError(err instanceof Error && err.message ? err.message : 'キャプチャを開始できませんでした');
    } finally {
      setSubmitting(null);
    }
  };

  const shownError = formError ?? error ?? null;

  return createPortal(
    <div className="modal-overlay modal-overlay--nested" onClick={locked ? undefined : onClose}>
      <div className="modal-card modal-card--md" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="url-capture-title">
        <div className="modal-header">
          <h2 className="modal-title" id="url-capture-title">{title}</h2>
          <button type="button" className="modal-close-button" onClick={onClose} aria-label="閉じる">×</button>
        </div>
        <div className={styles.body}>
          {notice && <div className={styles.notice}>{notice}</div>}
          {description && <div className={styles.hint}>{description}</div>}
          <div className={styles.field}>
            <div className={styles.fieldLabelRow}>
              <label className={styles.fieldLabel} htmlFor="url-capture-url">URL</label>
              <HelpTooltip ariaLabel="取得できるページのヘルプ">
                公開されているページだけを取得できます。ログインが必要なページや会員限定のページは取得できません。
              </HelpTooltip>
            </div>
            <input
              id="url-capture-url"
              type="url"
              inputMode="url"
              className={styles.input}
              placeholder="https://example.com/..."
              value={url}
              onChange={(e) => { setUrl(e.target.value); setFormError(null); }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || isComposingEnter(e)) return;
                e.preventDefault();
                void handleSubmit(enterIndex);
              }}
              disabled={locked}
              autoFocus
            />
          </div>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>端末</span>
            <div className={styles.segments} role="radiogroup" aria-label="端末">
              {choices.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={choice === value}
                  className={`${styles.segment} ${choice === value ? styles.segmentSelected : ''}`}
                  onClick={() => setChoice(value)}
                  disabled={locked}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {busy && busyText && <div className={styles.progress} role="status">{busyText}</div>}
          {shownError && <div className={styles.error} role="alert">{shownError}</div>}
        </div>
        <div className={styles.footer}>
          <button type="button" className="btn-cancel" onClick={onClose}>
            {busy ? '閉じる' : 'キャンセル'}
          </button>
          <div className={styles.footerActions}>
            {actions.map((action, index) => {
              const button = (
                <button
                  type="button"
                  className={action.primary ? 'btn-modal' : 'btn-modal-secondary'}
                  onClick={() => void handleSubmit(index)}
                  disabled={locked || !url.trim()}
                >
                  {submitting === index ? '開始しています…' : action.label}
                </button>
              );
              return action.tooltip
                ? <Tooltip key={action.label} content={action.tooltip}>{button}</Tooltip>
                : <span key={action.label}>{button}</span>;
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
